/**
 * FIELD-TEST BUG 3 — the stash consumption point.
 *
 * The signed-out athlete's invite code is stashed on /v2/landing before they
 * sign in (see `lib/team/joinStash.ts` for why the query param cannot make
 * the trip itself). OAuth returns them to /v2/today on web, and on native the
 * `tayro://auth-callback` deep link completes the session wherever the shell
 * happens to be. Neither destination knows about the invite — so consumption
 * lives here, mounted once beside the other null-rendering app mounts, rather
 * than being bolted onto whichever route OAuth chose today.
 *
 * It renders nothing and does nothing until BOTH a session and a stashed code
 * exist, so every ordinary launch is inert. On a hit it forwards to
 * `/v2/team?join=<code>`, which is the flow that already exists: the team
 * route pre-fills <JoinTeamModal> and the athlete presses "Join team". No new
 * screen, and no silent join — the confirmation the flow has always asked for
 * is still the thing that writes.
 *
 * **FIELD-TEST F1 — universal links reuse this same consumption point.** A
 * tapped `tayro.run/join/<code>` writes the identical stash (see
 * `lib/team/joinDeepLink.ts`), so a cold-started app consumes it on this
 * mount for free. The one case the mount effect alone cannot see is a link
 * arriving at an app that is ALREADY open and signed in — nothing navigates
 * and no dependency changes — so the deep-link handler fires
 * `JOIN_STASH_EVENT` and the second effect below re-runs the same check. It
 * is a wake-up, not a second code path: both call `consume()`.
 */

import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { JOIN_STASH_EVENT } from '../../lib/team/joinDeepLink';
import { takeStashedJoinCode } from '../../lib/team/joinStash';

export function JoinCodeMount(): null {
  const { session, loading } = useAuthContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const consume = useCallback(() => {
    if (loading || !session) return;
    // /team owns the `?join=` handoff; never race the route that consumes it.
    if (pathname.startsWith('/team')) return;
    const code = takeStashedJoinCode();
    if (!code) return;
    navigate(`/team?join=${encodeURIComponent(code)}`, { replace: true });
  }, [loading, session, pathname, navigate]);

  useEffect(() => {
    consume();
  }, [consume]);

  useEffect(() => {
    window.addEventListener(JOIN_STASH_EVENT, consume);
    return () => window.removeEventListener(JOIN_STASH_EVENT, consume);
  }, [consume]);

  return null;
}
